import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { typography, spacing } from '../../tokens';

export type StatusTagVariant = 'success' | 'neutral' | 'warning' | 'error' | 'info';

export interface StatusTagProps {
  /**
   * The status variant that determines the color scheme
   */
  variant: StatusTagVariant;

  /**
   * The text content to display
   */
  text: string;

  /**
   * Test ID for testing
   */
  testID?: string;
}

const variantStyles: Record<StatusTagVariant, { backgroundColor: string; textColor: string }> = {
  success: {
    backgroundColor: '#D3F4E7',
    textColor: '#24835D',
  },
  neutral: {
    backgroundColor: '#ECEEF1',
    textColor: '#4F5667',
  },
  warning: {
    backgroundColor: '#FFF4C1',
    textColor: '#D7A321',
  },
  error: {
    backgroundColor: '#FFD9D2',
    textColor: '#E24D2A',
  },
  info: {
    backgroundColor: '#D0E5F9',
    textColor: '#4A90E2',
  },
};

export const StatusTag: React.FC<StatusTagProps> = ({
  variant,
  text,
  testID,
}) => {
  const { backgroundColor, textColor } = variantStyles[variant];

  return (
    <View
      style={[styles.container, { backgroundColor }]}
      testID={testID}
      accessibilityRole="text"
      accessibilityLabel={`${variant} status: ${text}`}
    >
      <Text style={[styles.text, { color: textColor }]}>{text}</Text>
    </View>
  );
};

StatusTag.displayName = 'StatusTag';

const styles = StyleSheet.create({
  container: {
    height: 24,
    paddingHorizontal: spacing.xs, // 8px
    paddingVertical: spacing.xxs, // 4px
    borderRadius: 99, // Full pill shape
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
  },
  text: {
    ...typography.body.sm,
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
  },
});
